import React from 'react';
import { motion } from 'framer-motion';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Shield, Lock, Eye, Server, FileText, Mail } from 'lucide-react';
const sections = [{
  id: 'collection',
  title: 'Data Collection Protocols',
  icon: Server,
  content: `We collect information you provide directly to us when you create a CASINO account, update your profile, or communicate with us. This includes your neural-link ID, email address, and gaming preferences. We also automatically collect telemetry data from your gaming sessions to optimize network latency and rendering performance.`
}, {
  id: 'usage',
  title: 'Information Usage',
  icon: Eye,
  content: `Your data is processed to deliver immersive gaming experiences, personalize your interface, and maintain the integrity of our quantum network. We use machine learning algorithms to detect anomalies and prevent unauthorized access to your digital assets. We do not sell your personal data to third-party advertisers.`
}, {
  id: 'sharing',
  title: 'Data Sharing & Transfer',
  icon: FileText,
  content: `We may share your information with our affiliated game studios (e.g., Neon Forge, Cyber Synapse) solely for the purpose of enabling cross-platform progression. All data transfers are encrypted using military-grade protocols. We may disclose information if required by law or to protect the rights and safety of our users.`
}, {
  id: 'security',
  title: 'System Security',
  icon: Lock,
  content: `We implement multi-layered security measures, including end-to-end encryption and biometric authentication, to safeguard your information. However, no transmission over the global network is completely secure. You acknowledge the inherent risks of digital connectivity.`
}, {
  id: 'rights',
  title: 'User Rights',
  icon: Shield,
  content: `You have the right to access, correct, or delete your personal data stored on our servers. You may also opt-out of certain data processing activities, such as neural telemetry analysis. Contact our Data Protection Officer to exercise these rights.`
}];
export function PrivacyPage() {
  return <div className="min-h-screen bg-[#050814] text-white selection:bg-[#ff006e] selection:text-white scanlines font-mono" data-id="element-112">
      <Navbar data-id="element-113" />

      <main className="pt-20" data-id="element-114">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden" data-id="element-115">
          <div className="absolute inset-0 cyber-grid opacity-20" data-id="element-116" />
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-[#050814] via-transparent to-[#050814]" data-id="element-117" />

          <div className="container mx-auto px-6 relative z-10" data-id="element-118">
            <div className="max-w-4xl mx-auto text-center" data-id="element-119">
              <motion.div initial={{
              opacity: 0,
              y: -20
            }} animate={{
              opacity: 1,
              y: 0
            }} className="inline-block mb-4 px-3 py-1 border border-[#00f0ff]/30 bg-[#00f0ff]/10 rounded text-[#00f0ff] text-xs tracking-widest uppercase" data-id="element-120">
                Legal Document 77-B
              </motion.div>

              <motion.h1 initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 0.1
            }} className="text-5xl md:text-7xl font-black mb-6 font-['Orbitron']" data-id="element-121">
                PRIVACY{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00f0ff] to-[#ff006e]" data-id="element-122">
                  POLICY
                </span>
              </motion.h1>

              <motion.p initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.2
            }} className="text-gray-400 text-sm md:text-base max-w-2xl mx-auto" data-id="element-123">
                LAST UPDATED:{' '}
                <span className="text-[#ff006e]" data-id="element-124">AUGUST 15, 2077</span>
              </motion.p>
            </div>
          </div>
        </section>

        <div className="container mx-auto px-6 pb-24" data-id="element-125">
          <div className="flex flex-col lg:flex-row gap-12 max-w-6xl mx-auto" data-id="element-126">
            {/* Table of Contents Sidebar */}
            <aside className="lg:w-1/4" data-id="element-127">
              <div className="sticky top-24 p-6 border border-[#ffffff10] bg-[#0a0e27]/50 backdrop-blur-sm" data-id="element-128">
                <h3 className="text-[#00f0ff] font-bold mb-4 font-['Orbitron'] tracking-wider" data-id="element-129">
                  CONTENTS
                </h3>
                <nav className="space-y-3" data-id="element-130">
                  {sections.map((section, index) => <a key={section.id} href={`#${section.id}`} className="block text-sm text-gray-400 hover:text-white hover:pl-2 transition-all duration-300 border-l-2 border-transparent hover:border-[#ff006e]" data-id="element-131">
                      0{index + 1}. {section.title}
                    </a>)}
                  <a href="#contact" className="block text-sm text-gray-400 hover:text-white hover:pl-2 transition-all duration-300 border-l-2 border-transparent hover:border-[#ff006e]" data-id="element-132">
                    06. Contact Us
                  </a>
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <div className="lg:w-3/4 space-y-12" data-id="element-133">
              {/* Introduction */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} className="p-8 border-l-4 border-[#00f0ff] bg-[#0a0e27]/30 backdrop-blur-sm relative overflow-hidden" data-id="element-134">
                <div className="absolute top-0 right-0 p-2 opacity-20" data-id="element-135">
                  <Shield className="w-24 h-24 text-[#00f0ff]" data-id="element-136" />
                </div>
                <p className="text-lg leading-relaxed text-gray-300 relative z-10" data-id="element-137">
                  CASINO Gaming Corp ("CASINO", "we", "our") is committed to
                  protecting your digital footprint across the metaverse. This
                  Privacy Policy describes how we collect, process, and secure
                  your personal data when you access our quantum network and
                  gaming services. By initializing a connection to CASINO, you
                  consent to the protocols outlined below.
                </p>
              </motion.div>

              {/* Sections */}
              {sections.map((section, index) => <motion.section key={section.id} id={section.id} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="relative group" data-id="element-138">
                  {/* Section Header */}
                  <div className="flex items-center gap-4 mb-6" data-id="element-139">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#ff006e]/10 border border-[#ff006e] text-[#ff006e]" data-id="element-140">
                      <section.icon className="w-6 h-6" data-id="element-141" />
                    </div>
                    <h2 className="text-2xl font-bold font-['Orbitron'] text-white" data-id="element-142">
                      <span className="text-[#ff006e] mr-2" data-id="element-143">0{index + 1}.</span>
                      {section.title}
                    </h2>
                  </div>

                  {/* Section Content Card */}
                  <div className="relative p-8 border border-[#ffffff10] bg-[#0a0e27]/30 hover:bg-[#0a0e27]/50 transition-colors duration-500" data-id="element-144">
                    {/* Corner Accents */}
                    <div className="absolute -top-1 -left-1 w-4 h-4 border-t-2 border-l-2 border-[#00f0ff]" data-id="element-145" />
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 border-b-2 border-r-2 border-[#00f0ff]" data-id="element-146" />

                    <p className="text-gray-400 leading-relaxed" data-id="element-147">
                      {section.content}
                    </p>
                  </div>
                </motion.section>)}

              {/* Contact Section */}
              <motion.section id="contact" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} className="mt-16 pt-8 border-t border-[#ffffff10]" data-id="element-148">
                <h2 className="text-2xl font-bold font-['Orbitron'] mb-6" data-id="element-149">
                  06. Contact Us
                </h2>
                <div className="flex flex-col md:flex-row gap-6" data-id="element-150">
                  <div className="flex-1 p-6 border border-[#ffffff10] bg-[#0a0e27]/30" data-id="element-151">
                    <Mail className="w-6 h-6 text-[#00f0ff] mb-4" data-id="element-152" />
                    <h3 className="font-bold mb-2" data-id="element-153">General Inquiries</h3>
                    <p className="text-sm text-gray-400 mb-4" data-id="element-154">
                      For general questions about our privacy practices.
                    </p>
                    <a href="mailto:privacy@CASINO.WEBSITE" className="text-[#00f0ff] hover:text-white transition-colors" data-id="element-155">
                      AD@CASINO.WEBSITE
                    </a>
                  </div>
                  <div className="flex-1 p-6 border border-[#ffffff10] bg-[#0a0e27]/30" data-id="element-156">
                    <Shield className="w-6 h-6 text-[#ff006e] mb-4" data-id="element-157" />
                    <h3 className="font-bold mb-2" data-id="element-158">Data Protection Officer</h3>
                    <p className="text-sm text-gray-400 mb-4" data-id="element-159">
                      For specific data requests and legal concerns.
                    </p>
                    <a href="mailto:dpo@CASINO.WEBSITE" className="text-[#ff006e] hover:text-white transition-colors" data-id="element-160">
                      ADMIN1@CASINO.WEBSITE
                    </a>
                  </div>
                </div>
              </motion.section>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-161" />
    </div>;
}